import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  Output,
  EventEmitter
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FormStateControl } from '../../form-handling/form-state-control';
import { FieldTypes } from '../../utils/field-types.enum';
import {
  DynamicProperties,
  DynamicPropertyOptions
} from './dynamic-properties.model';
import { DynamicPropertyService } from './dynamic-properties.service';

@Component({
  selector: 'cs-dynamic-properties',
  standalone: false,
  templateUrl: './dynamic-properties.component.html',
  styleUrls: ['./dynamic-properties.component.scss'],
  providers: [DynamicPropertyService]
})
export class DynamicPropertiesComponent extends FormStateControl
  implements OnInit, OnChanges, OnDestroy {
  @Input()
  public infoForm: FormGroup;
  @Input()
  public dynamicFields: Array<DynamicProperties> = [];
  @Input() public clickedSubmit: boolean;
  @Output()
  public formLoaded = new EventEmitter<boolean>();
  public fieldTypes = FieldTypes;
  public formData: FormData = new FormData();
  public isError: boolean = false;
  public errMsg: string;

  constructor(
    private formBuilder: FormBuilder,
    private dynamicPropertyService: DynamicPropertyService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.toFormGroup();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('dynamicFields' in changes && this.infoForm && this.infoForm.controls) {
      if (this.infoForm.controls.hasOwnProperty('dynamicFields')) {
        this.updateForGroup();
      } else {
        this.toFormGroup();
      }
    }
  }

  public ngOnDestroy(): void {
    this.infoForm.removeControl('dynamicFields');
    this.infoForm.updateValueAndValidity();
  }

  public toFormGroup(): void {
    if (!this.infoForm) {
      return;
    }
    const group: FormGroup = this.formBuilder.group({});
    for (const dynamicField of this.dynamicFields) {
      if (dynamicField.fieldType === FieldTypes.CheckboxList) {
        const opts: FormGroup = this.formBuilder.group({});
        for (const opt of dynamicField.options) {
          opts.addControl(
            opt.value.toString(),
            new FormControl(
              opt.selectedValue ? JSON.parse(opt.selectedValue) : false
            )
          );
        }
        group.addControl(dynamicField.formPropertyId.toString(), opts);
      } else if (dynamicField.fieldType === FieldTypes.Checkbox) {
        group.addControl(
          dynamicField.formPropertyId.toString(),
          new FormControl(
            dynamicField.value ? JSON.parse(dynamicField.value) : false
          )
        );
      } else if (
        dynamicField.fieldType === FieldTypes.Dropdown ||
        dynamicField.fieldType === FieldTypes.RadioButton
      ) {
        group.addControl(
          dynamicField.formPropertyId.toString(),
          new FormControl(
            dynamicField.value ? parseInt(dynamicField.value, 0) : ''
          )
        );
      } else if (dynamicField.fieldType === FieldTypes.FileUpload) {
        const opts: FormGroup = this.formBuilder.group({});
        opts.addControl(
          'file',
          new FormControl(dynamicField.value ? dynamicField.value : '')
        );
        opts.addControl(
          'filename',
          new FormControl(dynamicField.fileName ? dynamicField.fileName : '')
        );
        group.addControl(dynamicField.formPropertyId.toString(), opts);
      } else {
        group.addControl(
          dynamicField.formPropertyId.toString(),
          new FormControl(
            dynamicField.value ? dynamicField.value.toString() : ''
          )
        );
      }
    }
    this.infoForm.addControl('dynamicFields', group);
    this.formLoaded.emit(true);
  }

  public getFileExtensions(acceptFiles: Array<DynamicPropertyOptions>): string {
    return acceptFiles.map(t => t.label).join();
  }

  public fileChange(
    files: Array<File>,
    controlName: string,
    acceptFiles: Array<DynamicPropertyOptions>
  ): void {
    if (files && files.length > 0) {
      const validFile = acceptFiles.find(
        x => x.label === '.' + files[0].name.split('.').pop()!
      )
        ? true
        : false;
      if (validFile) {
        this.isError = false;
        this.formData.append('File', files[0]);
        this.dynamicPropertyService
          .saveDynamicPropertyFiles({ File: files[0] })
          .subscribe(res => {
            this.infoForm
              .get('dynamicFields')!
              .get(controlName)!
              .get('filename')!
              .setValue(files[0].name);
            this.infoForm
              .get('dynamicFields')!
              .get(controlName)!
              .get('file')!
              .setValue(res);
          });
      } else {
        this.errMsg =
          'Invalid File, Accepted Formats are ' +
          acceptFiles.map(t => t.label).join() +
          '';
        this.isError = true;
      }
    }
  }

  public isFormInvalid(name: number): boolean {
    const control = this.getControl('dynamicFields');

    return (
      (control.get(name.toString())!.invalid &&
        control.get(name.toString())!.touched) ||
      (control.get(name.toString())!.invalid && this.clickedSubmit)
    );
  }

  private updateForGroup(): void {
    const form = this.infoForm.get('dynamicFields')!;
    for (const dynamicField of this.dynamicFields) {
      if (dynamicField.fieldType === FieldTypes.CheckboxList) {
        const group = form.get(dynamicField.formPropertyId.toString())!;
        for (const opt of dynamicField.options) {
          group.get(opt.value.toString())!.setValue(opt.selectedValue);
        }
      } else {
        form
          .get(dynamicField.formPropertyId.toString())!
          .setValue(dynamicField.value);
      }
    }
  }
}
